import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Tooltip,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CloudSync as HisIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  PlayArrow as TestIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import apiClient from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// TypeScript interfaces
interface HisConfig {
  id: string;
  institution_id: string;
  institution_name?: string;
  his_type: 'elogin' | 'easymedical';
  is_active: boolean;
  credentials_masked: Record<string, any>;
  config: Record<string, any>;
  last_check_at?: string;
  last_check_ok?: boolean;
  last_error?: string;
  created_at?: string;
  updated_at?: string;
}

interface HisConfigAdmin extends HisConfig {
  credentials: Record<string, any>;
}

interface Institution {
  id: string;
  name: string;
  tenant_id: string;
}

interface HisFormData {
  institution_id: string;
  his_type: 'elogin' | 'easymedical';
  is_active: boolean;
  // eLogin fields
  elogin_host: string;
  elogin_port: number;
  elogin_database: string;
  elogin_user: string;
  elogin_password: string;
  elogin_timeout: number;
  // EasyMedical fields
  easymedical_api_url: string;
  easymedical_secret_key: string;
  easymedical_timeout: number;
  easymedical_auto_accept: boolean;
  easymedical_poll_enabled: boolean;
  easymedical_poll_interval: number;
}

const defaultFormData: HisFormData = {
  institution_id: '',
  his_type: 'elogin',
  is_active: true,
  // eLogin defaults
  elogin_host: '',
  elogin_port: 1433,
  elogin_database: '',
  elogin_user: '',
  elogin_password: '',
  elogin_timeout: 30,
  // EasyMedical defaults
  easymedical_api_url: '',
  easymedical_secret_key: '',
  easymedical_timeout: 30,
  easymedical_auto_accept: true,
  easymedical_poll_enabled: true,
  easymedical_poll_interval: 120,
};

const AdminHisPage: React.FC = () => {
  const { user } = useAuth();
  const [hisList, setHisList] = useState<HisConfig[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedHis, setSelectedHis] = useState<HisConfigAdmin | null>(null);
  const [formData, setFormData] = useState<HisFormData>(defaultFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [dialogLoading, setDialogLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testResult, setTestResult] = useState<{
    success?: boolean;
    message?: string;
    latency_ms?: number;
  } | null>(null);
  const [testing, setTesting] = useState(false);

  // Load data
  const loadData = async () => {
    try {
      setLoading(true);

      // Load HIS configurations
      const hisResponse = await apiClient.get<{ configs: HisConfig[]; total: number }>('/his-config');
      if (hisResponse.data?.configs) {
        setHisList(hisResponse.data.configs);
      }

      // Load institutions
      const institutionsResponse = await apiClient.get<Institution[]>('/tenants/institutions');
      if (institutionsResponse.data && Array.isArray(institutionsResponse.data)) {
        setInstitutions(institutionsResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Get institutions without HIS configured
  const getAvailableInstitutions = () => {
    const configuredIds = hisList.map(h => h.institution_id);
    return institutions.filter(i => !configuredIds.includes(i.id));
  };

  // Filtered list
  const filteredHis = hisList.filter(his =>
    his.institution_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    his.his_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const stats = {
    total: hisList.length,
    active: hisList.filter(h => h.is_active).length,
    elogin: hisList.filter(h => h.his_type === 'elogin').length,
    easymedical: hisList.filter(h => h.his_type === 'easymedical').length,
    withErrors: hisList.filter(h => h.last_check_ok === false).length,
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.institution_id) {
      errors.institution_id = 'Selectați o instituție';
    }

    if (formData.his_type === 'elogin') {
      if (!formData.elogin_host) errors.elogin_host = 'Host obligatoriu';
      if (!formData.elogin_database) errors.elogin_database = 'Database obligatoriu';
      if (!formData.elogin_user) errors.elogin_user = 'User obligatoriu';
      if (!formData.elogin_password && !selectedHis) errors.elogin_password = 'Parolă obligatorie';
    } else {
      if (!formData.easymedical_api_url) errors.easymedical_api_url = 'API URL obligatoriu';
      if (!formData.easymedical_secret_key && !selectedHis) errors.easymedical_secret_key = 'Secret Key obligatoriu';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Build credentials object
  const buildCredentials = () => {
    if (formData.his_type === 'elogin') {
      return {
        host: formData.elogin_host,
        port: formData.elogin_port,
        database: formData.elogin_database,
        user: formData.elogin_user,
        password: formData.elogin_password,
      };
    } else {
      return {
        api_url: formData.easymedical_api_url,
        secret_key: formData.easymedical_secret_key,
      };
    }
  };

  // Build config object
  const buildConfig = () => {
    if (formData.his_type === 'elogin') {
      return {
        timeout: formData.elogin_timeout,
      };
    } else {
      return {
        timeout: formData.easymedical_timeout,
        auto_accept_requests: formData.easymedical_auto_accept,
        poll_enabled: formData.easymedical_poll_enabled,
        poll_interval_seconds: formData.easymedical_poll_interval,
      };
    }
  };

  // Test connection
  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const credentials = buildCredentials();
      // Only send non-empty password
      if (formData.his_type === 'elogin' && !formData.elogin_password && selectedHis) {
        credentials.password = selectedHis.credentials.password;
      }
      if (formData.his_type === 'easymedical' && !formData.easymedical_secret_key && selectedHis) {
        credentials.secret_key = selectedHis.credentials.secret_key;
      }

      const response = await apiClient.post('/his-config/test-connection', {
        his_type: formData.his_type,
        credentials,
      });

      setTestResult(response.data);
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Eroare la testare',
      });
    } finally {
      setTesting(false);
    }
  };

  // Create HIS config
  const handleCreateHis = async () => {
    if (!validateForm()) return;

    try {
      setDialogLoading(true);

      const createData = {
        institution_id: formData.institution_id,
        his_type: formData.his_type,
        is_active: formData.is_active,
        credentials: buildCredentials(),
        config: buildConfig(),
      };

      await apiClient.post('/his-config', createData);
      setShowCreateDialog(false);
      resetForm();
      loadData();
    } catch (error: any) {
      console.error('Error creating HIS config:', error);
      alert(error.response?.data?.detail || 'Eroare la creare');
    } finally {
      setDialogLoading(false);
    }
  };

  // Update HIS config
  const handleUpdateHis = async () => {
    if (!validateForm() || !selectedHis) return;

    try {
      setDialogLoading(true);

      const credentials = buildCredentials();
      // Remove empty password to keep existing
      if (formData.his_type === 'elogin' && !formData.elogin_password) {
        delete (credentials as any).password;
      }
      if (formData.his_type === 'easymedical' && !formData.easymedical_secret_key) {
        delete (credentials as any).secret_key;
      }

      const updateData = {
        is_active: formData.is_active,
        credentials,
        config: buildConfig(),
      };

      await apiClient.put(`/his-config/${selectedHis.id}`, updateData);
      setShowEditDialog(false);
      resetForm();
      loadData();
    } catch (error: any) {
      console.error('Error updating HIS config:', error);
      alert(error.response?.data?.detail || 'Eroare la actualizare');
    } finally {
      setDialogLoading(false);
    }
  };

  // Delete HIS config
  const handleDeleteHis = async (his: HisConfig) => {
    if (!window.confirm(`Ștergeți configurația HIS pentru "${his.institution_name}"?`)) {
      return;
    }

    try {
      await apiClient.delete(`/his-config/${his.id}`);
      loadData();
    } catch (error: any) {
      console.error('Error deleting HIS config:', error);
      alert(error.response?.data?.detail || 'Eroare la ștergere');
    }
  };

  // Test existing config
  const handleTestExisting = async (his: HisConfig) => {
    try {
      const response = await apiClient.post(`/his-config/${his.id}/test`);
      if (response.data.success) {
        alert('✅ Conexiune reușită!');
      } else {
        alert(`❌ Conexiune eșuată: ${response.data.message}`);
      }
      loadData(); // Refresh to show updated status
    } catch (error: any) {
      alert(`Eroare: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData(defaultFormData);
    setFormErrors({});
    setSelectedHis(null);
    setShowPassword(false);
    setTestResult(null);
  };

  // Open create dialog
  const handleOpenCreateDialog = () => {
    resetForm();
    const available = getAvailableInstitutions();
    if (available.length > 0) {
      setFormData(prev => ({ ...prev, institution_id: available[0].id }));
    }
    setShowCreateDialog(true);
  };

  // Open edit dialog
  const handleOpenEditDialog = async (his: HisConfig) => {
    try {
      const response = await apiClient.get<HisConfigAdmin>(`/his-config/${his.id}/admin`);
      const adminData = response.data;
      setSelectedHis(adminData);

      // Populate form
      setFormData({
        ...defaultFormData,
        institution_id: adminData.institution_id,
        his_type: adminData.his_type,
        is_active: adminData.is_active,
        // eLogin
        elogin_host: adminData.credentials.host || '',
        elogin_port: adminData.credentials.port || 1433,
        elogin_database: adminData.credentials.database || '',
        elogin_user: adminData.credentials.user || '',
        elogin_password: '', // Don't show existing password
        elogin_timeout: adminData.config?.timeout || 30,
        // EasyMedical
        easymedical_api_url: adminData.credentials.api_url || '',
        easymedical_secret_key: '', // Don't show existing key
        easymedical_timeout: adminData.config?.timeout || 30,
        easymedical_auto_accept: adminData.config?.auto_accept_requests ?? true,
        easymedical_poll_enabled: adminData.config?.poll_enabled ?? true,
        easymedical_poll_interval: adminData.config?.poll_interval_seconds || 120,
      });

      setShowEditDialog(true);
    } catch (error: any) {
      console.error('Error loading HIS config:', error);
      alert('Eroare la încărcarea configurației');
    }
  };

  // Open view dialog
  const handleOpenViewDialog = async (his: HisConfig) => {
    try {
      const response = await apiClient.get<HisConfigAdmin>(`/his-config/${his.id}/admin`);
      setSelectedHis(response.data);
      setShowViewDialog(true);
    } catch (error) {
      console.error('Error loading HIS config:', error);
    }
  };

  // Get institution name
  const getInstitutionName = (institutionId: string) => {
    const inst = institutions.find(i => i.id === institutionId);
    return inst?.name || 'Necunoscută';
  };

  // Render connection status
  const renderConnectionStatus = (his: HisConfig) => {
    if (his.last_check_at === null || his.last_check_at === undefined) {
      return <Chip label="Netestat" size="small" color="default" />;
    }
    if (his.last_check_ok) {
      return (
        <Tooltip title={`Ultima verificare: ${new Date(his.last_check_at).toLocaleString('ro-RO')}`}>
          <Chip label="OK" size="small" color="success" icon={<CheckIcon />} />
        </Tooltip>
      );
    }
    return (
      <Tooltip title={his.last_error || 'Eroare conexiune'}>
        <Chip label="Eroare" size="small" color="error" icon={<ErrorIcon />} />
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Compact styles
  const inputSx = { '& .MuiInputBase-root': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } };
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { color: 'text.secondary', fontSize: '0.85rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
    '& .MuiOutlinedInput-root': {
      fontSize: '0.875rem',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    },
    '& .MuiSelect-select': { color: 'text.primary' },
    '& .MuiMenuItem-root': { fontSize: '0.875rem' },
  };
  const tableSx = { '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' }, '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 }, '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' } };

  return (
    <Box p={1.5}>
      {/* Header - Compact */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Actualizează</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog} disabled={getAvailableInstitutions().length === 0} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>Adaugă HIS</Button>
      </Box>

      {/* Stats - Compact cu etichete vizibile */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        {[
          { icon: <HisIcon sx={{ fontSize: 20, color: 'primary.main' }} />, val: stats.total, label: 'Total HIS' },
          { icon: <CheckIcon sx={{ fontSize: 20, color: 'success.main' }} />, val: stats.active, label: 'Active' },
          { icon: <BusinessIcon sx={{ fontSize: 20, color: 'info.main' }} />, val: stats.elogin, label: 'eLogin' },
          { icon: <HisIcon sx={{ fontSize: 20, color: 'secondary.main' }} />, val: stats.easymedical, label: 'EasyMed' },
          { icon: <WarningIcon sx={{ fontSize: 20, color: 'warning.main' }} />, val: stats.withErrors, label: 'Cu Erori' },
        ].map((s, i) => (
          <Paper key={i} sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 110 }}>
            {s.icon}
            <Box>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, lineHeight: 1 }}>{s.val}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}>{s.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Search - Compact */}
      <Paper sx={{ p: 1, mb: 1.5 }}>
        <TextField fullWidth placeholder="Caută după instituție sau tip HIS..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" sx={inputSx} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }} />
      </Paper>

      {/* Table - Compact */}
      <TableContainer component={Paper} sx={tableSx}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.900', '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 } }}>
              <TableCell>Instituție</TableCell>
              <TableCell>Tip HIS</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Conexiune</TableCell>
              <TableCell>Server/URL</TableCell>
              <TableCell>Ultima Verificare</TableCell>
              <TableCell align="center">Acțiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHis.map((his) => (
              <TableRow key={his.id} hover>
                <TableCell><Typography sx={{ fontSize: '0.78rem', fontWeight: 500 }}>{his.institution_name || getInstitutionName(his.institution_id)}</Typography></TableCell>
                <TableCell><Chip label={his.his_type === 'elogin' ? 'eLogin' : 'EasyMed'} size="small" color={his.his_type === 'elogin' ? 'primary' : 'secondary'} sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                <TableCell><Chip label={his.is_active ? 'Activ' : 'Inactiv'} size="small" color={his.is_active ? 'success' : 'default'} sx={{ height: 18, fontSize: '0.68rem' }} /></TableCell>
                <TableCell>{renderConnectionStatus(his)}</TableCell>
                <TableCell><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>{his.his_type === 'elogin' ? `${his.credentials_masked.host}:${his.credentials_masked.port}` : (his.credentials_masked.api_url?.substring(0, 30) + '...')}</Typography></TableCell>
                <TableCell><Typography sx={{ fontSize: '0.72rem' }}>{his.last_check_at ? new Date(his.last_check_at).toLocaleString('ro-RO') : '-'}</Typography></TableCell>
                <TableCell align="center">
                  <Tooltip title="Test"><IconButton size="small" onClick={() => handleTestExisting(his)} color="info"><TestIcon /></IconButton></Tooltip>
                  <Tooltip title="Vezi"><IconButton size="small" onClick={() => handleOpenViewDialog(his)}><ViewIcon /></IconButton></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenEditDialog(his)} color="primary"><EditIcon /></IconButton></Tooltip>
                  {user?.role === 'admin' && <Tooltip title="Șterge"><IconButton size="small" onClick={() => handleDeleteHis(his)} color="error"><DeleteIcon /></IconButton></Tooltip>}
                </TableCell>
              </TableRow>
            ))}
            {filteredHis.length === 0 && <TableRow><TableCell colSpan={7} align="center"><Typography sx={{ py: 2, fontSize: '0.8rem', color: 'text.secondary' }}>{hisList.length === 0 ? 'Nu există configurații HIS.' : 'Nicio configurație găsită.'}</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog - Compact & Consistent */}
      <Dialog open={showCreateDialog || showEditDialog} onClose={() => { setShowCreateDialog(false); setShowEditDialog(false); resetForm(); }} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>{showCreateDialog ? 'Adaugă Configurație HIS' : 'Editează Configurație HIS'}</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Institution & HIS Type - only for create */}
            {showCreateDialog && (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <FormControl fullWidth size="small" required error={!!formErrors.institution_id} sx={inputSx}>
                  <InputLabel>Instituție *</InputLabel>
                  <Select value={formData.institution_id} onChange={(e) => setFormData({ ...formData, institution_id: e.target.value })} label="Instituție *">
                    {getAvailableInstitutions().map((inst) => <MenuItem key={inst.id} value={inst.id} sx={{ fontSize: '0.8rem' }}>{inst.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={inputSx}>
                  <InputLabel>Tip HIS</InputLabel>
                  <Select value={formData.his_type} onChange={(e) => setFormData({ ...formData, his_type: e.target.value as 'elogin' | 'easymedical' })} label="Tip HIS">
                    <MenuItem value="elogin" sx={{ fontSize: '0.8rem' }}>eLogin (MSSQL)</MenuItem>
                    <MenuItem value="easymedical" sx={{ fontSize: '0.8rem' }}>EasyMedical (API)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
            {/* Show institution name for edit */}
            {showEditDialog && selectedHis && (
              <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>Editare HIS pentru: <strong>{selectedHis.institution_name}</strong> ({selectedHis.his_type})</Alert>
            )}
            {/* Active Switch */}
            <FormControlLabel control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} size="small" />} label={<Typography sx={{ fontSize: '0.78rem' }}>Configurație Activă</Typography>} />
            <Divider sx={{ my: 0.5 }}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{formData.his_type === 'elogin' ? 'Conexiune MSSQL (eLogin)' : 'Conexiune API (EasyMedical)'}</Typography></Divider>
            {/* eLogin Fields */}
            {formData.his_type === 'elogin' && (
              <>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField fullWidth label="Host (IP/Hostname) *" value={formData.elogin_host} onChange={(e) => setFormData({ ...formData, elogin_host: e.target.value })} error={!!formErrors.elogin_host} helperText={formErrors.elogin_host || 'Ex: 10.0.1.13'} size="small" sx={inputSx} />
                  <TextField label="Port" type="number" value={formData.elogin_port} onChange={(e) => setFormData({ ...formData, elogin_port: parseInt(e.target.value) || 1433 })} size="small" sx={{ ...inputSx, width: 100 }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField fullWidth label="Database *" value={formData.elogin_database} onChange={(e) => setFormData({ ...formData, elogin_database: e.target.value })} error={!!formErrors.elogin_database} helperText={formErrors.elogin_database || 'Ex: eLoginHospital2021_CTSIA'} size="small" sx={inputSx} />
                  <TextField fullWidth label="User *" value={formData.elogin_user} onChange={(e) => setFormData({ ...formData, elogin_user: e.target.value })} error={!!formErrors.elogin_user} helperText={formErrors.elogin_user} size="small" sx={inputSx} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField fullWidth label="Parolă" type={showPassword ? 'text' : 'password'} value={formData.elogin_password} onChange={(e) => setFormData({ ...formData, elogin_password: e.target.value })} error={!!formErrors.elogin_password} helperText={formErrors.elogin_password || (showEditDialog ? 'Gol = păstrează existentă' : '')} size="small" sx={inputSx} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small"><ViewIcon sx={{ fontSize: 16 }} /></IconButton></InputAdornment> }} />
                  <TextField label="Timeout (sec)" type="number" value={formData.elogin_timeout} onChange={(e) => setFormData({ ...formData, elogin_timeout: parseInt(e.target.value) || 30 })} size="small" sx={{ ...inputSx, width: 120 }} />
                </Box>
              </>
            )}
            {/* EasyMedical Fields */}
            {formData.his_type === 'easymedical' && (
              <>
                <TextField fullWidth label="API URL *" value={formData.easymedical_api_url} onChange={(e) => setFormData({ ...formData, easymedical_api_url: e.target.value })} error={!!formErrors.easymedical_api_url} helperText={formErrors.easymedical_api_url || 'Ex: https://api.easymedical.ro/v1'} size="small" sx={inputSx} />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField fullWidth label="Secret Key" type={showPassword ? 'text' : 'password'} value={formData.easymedical_secret_key} onChange={(e) => setFormData({ ...formData, easymedical_secret_key: e.target.value })} error={!!formErrors.easymedical_secret_key} helperText={formErrors.easymedical_secret_key || (showEditDialog ? 'Gol = păstrează existentă' : '')} size="small" sx={inputSx} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small"><ViewIcon sx={{ fontSize: 16 }} /></IconButton></InputAdornment> }} />
                  <TextField label="Timeout (sec)" type="number" value={formData.easymedical_timeout} onChange={(e) => setFormData({ ...formData, easymedical_timeout: parseInt(e.target.value) || 30 })} size="small" sx={{ ...inputSx, width: 120 }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel control={<Switch checked={formData.easymedical_auto_accept} onChange={(e) => setFormData({ ...formData, easymedical_auto_accept: e.target.checked })} size="small" />} label={<Typography sx={{ fontSize: '0.78rem' }}>Auto-Accept</Typography>} />
                  <FormControlLabel control={<Switch checked={formData.easymedical_poll_enabled} onChange={(e) => setFormData({ ...formData, easymedical_poll_enabled: e.target.checked })} size="small" />} label={<Typography sx={{ fontSize: '0.78rem' }}>Polling</Typography>} />
                  <TextField label="Poll Interval (sec)" type="number" value={formData.easymedical_poll_interval} onChange={(e) => setFormData({ ...formData, easymedical_poll_interval: parseInt(e.target.value) || 120 })} disabled={!formData.easymedical_poll_enabled} size="small" sx={{ ...inputSx, width: 130 }} />
                </Box>
              </>
            )}
            {/* Test Connection */}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button variant="outlined" startIcon={testing ? <CircularProgress size={14} /> : <TestIcon sx={{ fontSize: 16 }} />} onClick={handleTestConnection} disabled={testing} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Testează Conexiunea</Button>
              {testResult && <Alert severity={testResult.success ? 'success' : 'error'} sx={{ flex: 1, py: 0.25, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>{testResult.message}{testResult.latency_ms && ` (${testResult.latency_ms}ms)`}</Alert>}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); resetForm(); }} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Anulează</Button>
          <Button onClick={showCreateDialog ? handleCreateHis : handleUpdateHis} variant="contained" disabled={dialogLoading} size="small" startIcon={dialogLoading ? <CircularProgress size={14} /> : (showCreateDialog ? <AddIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />)} sx={{ fontSize: '0.78rem', textTransform: 'none' }}>{dialogLoading ? 'Se salvează...' : showCreateDialog ? 'Creează' : 'Actualizează'}</Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog - Compact & Consistent */}
      <Dialog open={showViewDialog} onClose={() => setShowViewDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider', color: 'text.primary' }}>Detalii Configurație HIS</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {selectedHis && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Instituție</Typography><Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{selectedHis.institution_name}</Typography></Box>
                <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Tip HIS</Typography><Chip label={selectedHis.his_type === 'elogin' ? 'eLogin' : 'EasyMed'} size="small" color={selectedHis.his_type === 'elogin' ? 'primary' : 'secondary'} sx={{ height: 20, fontSize: '0.7rem' }} /></Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Status</Typography><Chip label={selectedHis.is_active ? 'Activ' : 'Inactiv'} size="small" color={selectedHis.is_active ? 'success' : 'default'} sx={{ height: 20, fontSize: '0.7rem' }} /></Box>
                <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Conexiune</Typography>{renderConnectionStatus(selectedHis)}</Box>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Credențiale</Typography>
              {selectedHis.his_type === 'elogin' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Host</Typography><Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{selectedHis.credentials.host}:{selectedHis.credentials.port}</Typography></Box>
                    <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Database</Typography><Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{selectedHis.credentials.database}</Typography></Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>User</Typography><Typography sx={{ fontSize: '0.85rem' }}>{selectedHis.credentials.user}</Typography></Box>
                    <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Password</Typography><Typography sx={{ fontSize: '0.85rem' }}>********</Typography></Box>
                  </Box>
                </Box>
              )}
              {selectedHis.his_type === 'easymedical' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>API URL</Typography><Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{selectedHis.credentials.api_url}</Typography></Box>
                  <Box><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Secret Key</Typography><Typography sx={{ fontSize: '0.85rem' }}>********</Typography></Box>
                </Box>
              )}
              {selectedHis.last_error && <Alert severity="error" sx={{ py: 0.5, mt: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>Ultima Eroare: {selectedHis.last_error}</Alert>}
              <Box sx={{ mt: 1 }}><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Creat</Typography><Typography sx={{ fontSize: '0.85rem' }}>{selectedHis.created_at && new Date(selectedHis.created_at).toLocaleString('ro-RO')}</Typography></Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setShowViewDialog(false)} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Închide</Button>
          <Button variant="contained" startIcon={<EditIcon sx={{ fontSize: 16 }} />} onClick={() => { setShowViewDialog(false); if (selectedHis) { handleOpenEditDialog(selectedHis); } }} size="small" sx={{ fontSize: '0.78rem', textTransform: 'none' }}>Editează</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminHisPage;

